import { ActivityFilterKey, useWorkspaceAsset } from '@gripp/shared-logic';
import {
  ActivityFilterScreen,
  ActivityFilterScreenHandle,
  ActivityList,
  type ColumnData,
  SlideAction,
  SlideLayout,
  type SlideLayoutLocationState,
  ViewMode,
} from '@gripp/shared-ui';
import { useRef, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ActivityFilters } from '../../activity/components/activityFilters';
import { Issue } from '../../issues/components/issue';
import { useRoutineImagePanel } from '../../issues/components/useRoutineImagePanel';
import { HeaderResetButton } from '../../shared/components/headerResetButton';
import { AssetRouteProvider } from '../assetRouteContext';
import {
  ALL_ACTIVITIES_QUERY_KEY,
  ALL_ACTIVITIES_QUERY_VALUE,
  RoutineSubmitSource,
} from '../assetUtils';
import { AssetCard } from './assetCard';
import { AssetFilters } from './assetFilters';
import { AssetIntegrationReadingsSection } from './assetIntegrationReadingsSection';

const ASSET_ACTIVITY_DETAIL_PATH_PATTERN =
  /^\/assets\/[^/]+\/activity\/[^/]+\/?$/;
const ACTIVITY_ASSET_ACTIVITY_DETAIL_PATH_PATTERN =
  /^\/activities\/(?:digest\/[^/]+\/)?[^/]+\/asset\/[^/]+\/activity\/[^/]+\/?$/;
const WORKSPACE_ASSET_ACTIVITY_DETAIL_PATH_PATTERN =
  /^\/workspaces\/[^/]+\/asset\/[^/]+\/activity\/[^/]+\/?$/;

const getAssetCardBackTarget = (pathname: string) => {
  // Digest context (GRIPP #2887): /activities/digest/:digestKey/asset/... — the
  // asset card should return to the digest panel, not /activities/digest.
  const digestContextMatch = pathname.match(/^\/activities\/(digest\/[^/]+)/);
  if (digestContextMatch) {
    return `/activities/${digestContextMatch[1]}`;
  }

  const activitiesContextMatch = pathname.match(/^\/activities\/([^/]+)/);
  const workspacesContextMatch = pathname.match(/^\/workspaces\/([^/]+)/);

  return activitiesContextMatch
    ? `/activities/${activitiesContextMatch[1]}`
    : workspacesContextMatch
    ? `/workspaces/${workspacesContextMatch[1]}`
    : '/assets';
};

const usesAssetCardLeftPanel = (
  pathname: string,
  search: string,
  from: RoutineSubmitSource | undefined
) => {
  if (from === RoutineSubmitSource.AssetCard) return true;

  const cameFromActivitiesAll =
    new URLSearchParams(search).get('from') === ALL_ACTIVITIES_QUERY_VALUE;

  return (
    !cameFromActivitiesAll &&
    (ASSET_ACTIVITY_DETAIL_PATH_PATTERN.test(pathname) ||
      ACTIVITY_ASSET_ACTIVITY_DETAIL_PATH_PATTERN.test(pathname) ||
      WORKSPACE_ASSET_ACTIVITY_DETAIL_PATH_PATTERN.test(pathname))
  );
};

export const AssetActivity = () => {
  const { assetId, issueId, workspaceId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { workspaceAsset } = useWorkspaceAsset(assetId ?? '', !assetId);
  const filterRef = useRef<ActivityFilterScreenHandle>(null);
  const [showFilters, setShowFilters] = useState(false);
  // Routine media opens as its own column beside the submission being reviewed.
  const { onViewImages, imageColumn, closeImagePanel } =
    useRoutineImagePanel(issueId);
  const locationState = location.state as SlideLayoutLocationState | null;
  const initialTransition =
    locationState?.slideLayoutInitialTransition === SlideAction.NEXT ||
    locationState?.slideLayoutInitialTransition === SlideAction.PREV
      ? locationState.slideLayoutInitialTransition
      : undefined;

  if (!assetId) {
    return null;
  }

  // Recover the asset basePath (and whether we're in an activities context)
  // from the URL prefix. We can't trust useParams().issueId here: the
  // assetSubRoutes spread reuses `:issueId` for the outer and inner positions,
  // so under both the conversation and digest contexts it resolves to the
  // INNER activity id, not the outer context. Splitting on `/asset/:assetId`
  // yields the true prefix for either context.
  const assetSegment = `/asset/${assetId}`;
  const isActivitiesContext = location.pathname.startsWith('/activities/');
  const isWorkspaceContext = location.pathname.startsWith('/workspaces/');
  const assetBasePath =
    isActivitiesContext || isWorkspaceContext
      ? `${location.pathname.split(assetSegment)[0]}${assetSegment}`
      : `/assets/${assetId}`;

  const from: RoutineSubmitSource | undefined = location.state?.from;
  const cameFromAsset = usesAssetCardLeftPanel(
    location.pathname,
    location.search,
    from
  );
  // Opening or closing the filter clears the image panel, so the filter always
  // holds `third` and an image opened after it always holds `fourth`. Leaving
  // both open lets a slot swap out from under the gallery on the next toggle,
  // which remounts it at the wrong index and animates it out and in at once.
  const setFiltersOpen = (open: boolean) => {
    closeImagePanel();
    setShowFilters(open);
  };

  const handleLeftColumnBackPress = () => {
    if (cameFromAsset) {
      navigate(getAssetCardBackTarget(location.pathname), {
        state: { slideLayoutInitialTransition: SlideAction.PREV },
      });
      return;
    }

    navigate(workspaceId ? '../../../..' : '../..', {
      relative: 'path',
      state: { slideLayoutInitialTransition: SlideAction.PREV },
    });
  };

  const leftColumn: ColumnData = {
    title: 'assets.menu.title',
    rawTitle: workspaceAsset?.asset?.name,
    mainComponent: cameFromAsset ? (
      <AssetCard assetId={assetId} forceShowing />
    ) : (
      <LeftColumn assetId={assetId} selectedIssue={issueId} />
    ),
    headerComponent: cameFromAsset ? (
      <AssetFilters assetId={assetId} />
    ) : (
      <ActivityFilters onFilterChange={() => setFiltersOpen(!showFilters)} />
    ),
    showBackButton: true,
    disableScroll: cameFromAsset ? false : true,
    // Return to the asset-context panel; the destination layout owns the
    // reverse handoff animation.
    onBackButtonPress: handleLeftColumnBackPress,
    backButtonTransition: SlideAction.PREV,
    backButtonTransitionBehavior: 'concurrent' as const,
    backButtonTransitionExitColumn: cameFromAsset ? 'both' : 'right',
  };

  const rightColumn = {
    title: 'assets.menu.title',
    showHeader: false,
    disableScroll: true,
    mainComponent: (
      <Issue
        issueId={issueId}
        showAssetLink={false}
        onViewImages={onViewImages}
      />
    ),
  };

  // Filter panel lives in its own column so the activity detail (rightColumn)
  // keeps its scroll position when the user opens and closes the filter panel.
  // Toggling showFilters to false from onBackButtonPress lets React re-derive
  // the column as null on the next render, which collapses the panel.
  const filterColumn: ColumnData | null = showFilters
    ? {
        title: 'assets.menu.title',
        mainComponent: (
          <ActivityFilterScreen
            ref={filterRef}
            mode="page"
            isVisible={true}
            filterKey={ActivityFilterKey.AssetActivity}
            assetId={assetId}
            bottomBar={() => null}
          />
        ),
        headerComponent: (
          <HeaderResetButton onReset={() => filterRef.current?.onReset()} />
        ),
        showBackButton: true,
        onBackButtonPress: () => setFiltersOpen(false),
        disableScroll: true,
      }
    : null;

  const overlayColumns = [filterColumn, imageColumn].filter(
    (column): column is ColumnData => !!column
  );
  const thirdColumn: ColumnData | undefined = overlayColumns[0];
  const fourthColumn: ColumnData | undefined = overlayColumns[1];

  return (
    <AssetRouteProvider
      value={{
        // Derive the asset basePath from the URL prefix, not contextIssueId:
        // when this route is reached under the digest spread
        // (activities/digest/:digestKey/asset/:assetId/activity/:issueId) the
        // assetSubRoutes spread reuses `:issueId` for both the outer and inner
        // position, so useParams().issueId is the INNER activity id, not the
        // context. Splitting on `/asset/:assetId` recovers the true prefix
        // (covers the issue, digest, and workspace activity contexts).
        basePath: assetBasePath,
        listPath: workspaceId
          ? '/workspaces'
          : isActivitiesContext
          ? '/activities'
          : '/assets',
        isWorkspaceMode: !!workspaceId,
        issueId: undefined,
      }}
    >
      <SlideLayout
        leftColumn={leftColumn}
        rightColumn={rightColumn}
        thirdColumn={thirdColumn}
        fourthColumn={fourthColumn}
        viewMode={ViewMode.DUAL}
        initialTransition={initialTransition}
      />
    </AssetRouteProvider>
  );
};

const LeftColumn = ({
  assetId,
  selectedIssue,
}: {
  assetId: string;
  selectedIssue?: string;
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const onIssueSelected = (issueId: string) => {
    // For activities and workspace routes, preserve the context prefix by
    // extracting it from the current URL. Both share the `/asset/${assetId}`
    // segment, so splitting on it gives us the correct prefix in either case.
    const assetSegment = `/asset/${assetId}`;
    if (location.pathname.includes(assetSegment)) {
      const prefix = location.pathname.split(assetSegment)[0];
      navigate(
        `${prefix}${assetSegment}/activity/${issueId}?${ALL_ACTIVITIES_QUERY_KEY}`
      );
    } else {
      navigate(
        `/assets/${assetId}/activity/${issueId}?${ALL_ACTIVITIES_QUERY_KEY}`
      );
    }
  };

  return (
    <View style={leftColumnStyles.container}>
      <AssetIntegrationReadingsSection assetId={assetId} />
      <ActivityList
        filterKey={ActivityFilterKey.AssetActivity}
        onIssueSelected={onIssueSelected}
        assetId={assetId}
        showAssetName={false}
        showImage={false}
        pageSize={50}
        selectedIssue={selectedIssue}
        showTopBar={false}
      />
    </View>
  );
};

const leftColumnStyles = StyleSheet.create({
  container: {
    flex: 1,
  },
});
